import React from 'react';

import { Box } from '@rbilabs/universal-components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { LoadingIndicator } from 'features/store/pages/pickup-store-selector/components';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { BlankScreen } from 'components/blank-screen';
import { CardModal } from 'components/card-modal';
import { useRefundEligibility } from 'hooks/refund-eligibility';
import { useOrderDetails } from 'hooks/use-order-details';
import { useSupportData } from 'hooks/use-support-data';
import { LoyaltyLoader } from 'pages/loyalty/loyalty-loader';
import { CustomEventNames, logNavigationClick } from 'state/amplitude';
import { useOrderContext } from 'state/order';
import { useReorder } from 'state/order/hooks/use-reorder';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { CartEntry } from './cart-entry';
import { Divider, NeedHelpButtonLoadingIndicatorWrapper } from './details-modal.styled';
import { PointsEarnedBlock } from './points-earned';
import { ReceiptDetails } from './receipt-details';
import { ReceiptHeadInfo } from './receipt-head-info';
import { useHelpLinkUrlGenerator } from './use-help-link-url-generator';

export const DetailsModal: React.FC = () => {
  const router = useRouter();

  const { rbiOrderId } = useLocalSearchParams<{ rbiOrderId: string }>();

  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const { order, loading: isOrderLoading, refetch } = useOrderDetails(rbiOrderId);

  const { handleReorderClick, isReorderingDisabled } = useReorder(order);
  const {
    reorder: { reordering },
  } = useOrderContext();

  const { isDeliveryActive } = useRefundEligibility({
    orderId: rbiOrderId,
  });
  const { isSupportDataLoading } = useSupportData();

  const helpLink = useHelpLinkUrlGenerator(rbiOrderId);

  const loyaltyTransaction = order?.loyaltyTransaction;
  const handleNeedHelpClick = () => {
    logNavigationClick(CustomEventNames.BUTTON_CLICK_RECENT_ORDER_NEED_HELP);
    router.navigate(helpLink);
  };

  // TODO: ReactNavigation GST-92 Move away from Modal component
  return (
    <CardModal
      standardPadding
      analyticsEvent="Order Details"
      headerText={formatMessage({ id: 'receipt' })}
      scrollView
      footer={
        <>
          {isDeliveryActive ? (
            <ActionButton
              fullWidth
              onPress={() =>
                router.navigate(`${routes.orderConfirmation}/${order.rbiOrderId}?dismissOnly=true`)
              }
              isLoading={isOrderLoading}
            >
              {formatMessage({ id: 'trackOrder' })}
            </ActionButton>
          ) : (
            <ActionButton
              fullWidth
              onPress={() => {
                handleReorderClick();
              }}
              isLoading={reordering || isOrderLoading}
              disabled={isReorderingDisabled}
            >
              {formatMessage({ id: 'reorder' })}
            </ActionButton>
          )}
          <Box paddingY="$4" focusable>
            {formatMessage({
              id: 'pricesDiscountsAndAvailabilityAreSbujectToChangeWhenYouReorder',
            })}
          </Box>

          {isSupportDataLoading && (
            <NeedHelpButtonLoadingIndicatorWrapper>
              <LoadingIndicator />
            </NeedHelpButtonLoadingIndicatorWrapper>
          )}

          {/*
           * isSupportDataLoading becomes false quickly, so aria-live="polite" on this button is distracting.
           * This will silently appear for screen readers.
           */}
          {!isSupportDataLoading && (
            <ActionButton
              testID="recent-order-need-help"
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
              size={ActionButtonSizes.SMALL}
              onPress={handleNeedHelpClick}
            >
              {formatMessage({ id: 'needHelp' })}
            </ActionButton>
          )}
        </>
      }
    >
      {!isOrderLoading && order ? (
        <>
          <Box>
            <ReceiptHeadInfo order={order} />
            <Divider />
          </Box>
          {loyaltyTransaction && (
            <>
              <PointsEarnedBlock lylTransaction={loyaltyTransaction} />
              <Divider />
            </>
          )}
          <Box>
            {order.cart.cartEntries.map((entry, i) => {
              return (
                !entry.isDummyItem && (
                  <React.Fragment key={entry.name! + i}>
                    <CartEntry
                      currencyFormatter={formatCurrencyForLocale}
                      entry={entry}
                      index={i}
                    />
                    <Divider />
                  </React.Fragment>
                )
              );
            })}
          </Box>
          <Box>
            <ReceiptDetails order={order} />
          </Box>
        </>
      ) : isOrderLoading ? (
        <LoyaltyLoader />
      ) : (
        <BlankScreen context={{ reason: 'there is not rbiOrderId' }} onReload={refetch} />
      )}
    </CardModal>
  );
};
